import { type ReactNode, useState } from 'react';

import type { Vendor } from '@keboola/api-client/developerPortal/types';
import { Button, Modal, setSubmitError, toast, useAppForm } from '@keboola/design';

import { SaveButton } from '../../../components/SaveButton';
import { selectSaveButtonState } from '../../../components/saveButtonState';
import { getApiErrorMessage } from '../../auth/errors';
import { formValidators } from '../../auth/validate';
import { useAdminUpdateVendor, useCreateVendorAsAdmin } from '../queries';
import {
  adminVendorCreateFormSchema,
  adminVendorEditFormSchema,
  suggestVendorId,
} from '../schemas';

const FORM_ID = 'admin-vendor';

type Props = {
  /** Omit to create. */
  vendor?: Vendor;
  trigger: ReactNode;
};

/**
 * Adds a vendor, or edits one.
 *
 * - `id` exists only when creating — changing it later would orphan the vendor's components.
 * - While creating, `id` is prefilled from the name and stays editable.
 */
export function VendorFormModal({ vendor, trigger }: Props) {
  const isCreating = vendor === undefined;
  const [open, setOpen] = useState(false);
  const createVendor = useCreateVendorAsAdmin();
  const updateVendor = useAdminUpdateVendor();
  // Whether `id` has been taken over by hand; until it is, it follows the name.
  const [idIsManual, setIdIsManual] = useState(false);

  const form = useAppForm({
    defaultValues: {
      id: vendor?.id ?? '',
      name: vendor?.name ?? '',
      email: vendor?.email ?? '',
      address: vendor?.address ?? '',
      projectUrl: vendor?.projectUrl ?? '',
    },
    validators: formValidators(
      isCreating ? adminVendorCreateFormSchema : adminVendorEditFormSchema,
    ),
    canSubmitWhenInvalid: true,
    onSubmit: async ({ value, formApi }) => {
      try {
        if (isCreating) {
          await createVendor.mutateAsync({
            id: value.id,
            name: value.name,
            email: value.email.trim(),
            address: value.address,
          });
          toast.success(`${value.name} was added.`);
        } else {
          await updateVendor.mutateAsync({
            vendorId: vendor.id,
            input: {
              name: value.name,
              email: value.email.trim(),
              address: value.address,
              projectUrl: value.projectUrl,
            },
          });
          toast.success('The vendor details have been updated.');
        }
        setOpen(false);
      } catch (error) {
        setSubmitError(formApi, getApiErrorMessage(error, 'Could not save the vendor.'));
      }
    },
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          form.reset();
          setIdIsManual(false);
        }
      }}
    >
      <Modal.Trigger asChild>{trigger}</Modal.Trigger>
      <Modal.Content size="medium">
        <Modal.Header
          description={
            isCreating
              ? 'The vendor is created approved and ready to use.'
              : `Vendor ID ${vendor.id}.`
          }
        >
          {isCreating ? 'Add vendor' : 'Edit vendor'}
        </Modal.Header>
        <Modal.Body>
          <form.AppForm>
            <form.FormBody id={FORM_ID}>
              <form.AppField
                name="name"
                listeners={{
                  onChange: ({ value }) => {
                    if (isCreating && !idIsManual) {
                      form.setFieldValue('id', suggestVendorId(value));
                    }
                  },
                }}
              >
                {(field) => <field.TextField label="Name" required placeholder="Acme Co." />}
              </form.AppField>

              {isCreating && (
                <form.AppField
                  name="id"
                  listeners={{
                    // Manual = differs from the live suggestion; clearing an override hands it back to the name.
                    onChange: ({ value }) =>
                      setIdIsManual(value !== suggestVendorId(form.getFieldValue('name'))),
                  }}
                >
                  {(field) => (
                    <field.TextField
                      label="ID"
                      required
                      placeholder="acme-co"
                      help="Lowercase letters, digits and dashes. Every component id starts with this and it cannot be changed later."
                    />
                  )}
                </form.AppField>
              )}

              <form.AppField name="address">
                {(field) => <field.TextareaField label="Address" required />}
              </form.AppField>
              <form.AppField name="email">
                {(field) => <field.TextField label="Email" type="email" required />}
              </form.AppField>

              {!isCreating && (
                <form.AppField name="projectUrl">
                  {(field) => (
                    <field.TextField
                      label="Test project URL"
                      placeholder="https://connection.keboola.com/admin/projects/1234"
                      help="A Keboola project where this vendor's components can be tried out."
                    />
                  )}
                </form.AppField>
              )}

              <form.SubmitErrorAlert />
            </form.FormBody>
          </form.AppForm>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Close asChild>
            <Button type="button" variant="secondary">
              Cancel
            </Button>
          </Modal.Close>
          {/* No pristine gate on Add: a blank create form is "unchanged", so Add would never enable. */}
          {isCreating ? (
            <form.AppForm>
              <form.SubmitButton form={FORM_ID}>Add</form.SubmitButton>
            </form.AppForm>
          ) : (
            <form.Subscribe selector={selectSaveButtonState}>
              {(state) => (
                <SaveButton state={state} form={FORM_ID}>
                  Save
                </SaveButton>
              )}
            </form.Subscribe>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
